import React, { useState } from 'react';
import { Calendar, X } from 'lucide-react';
import appointmentService from '../../services/appointmentService';
import useAvailableSlots from '../../hooks/useAvailableSlots';
import SlotPicker from '../common/SlotPicker';
import { formatDoctorName } from '../../utils/doctorName';
import {
  formatAppointmentDate,
  formatAppointmentTime,
  getTodayInBogota,
  slotToDate,
} from '../../utils/appointmentSchedule';

// El id del médico llega de formas distintas según de dónde venga la cita
// (populada desde /appointments/my, o solo con el ObjectId).
const getDoctorId = (appointment) =>
  appointment?.doctor?._id || appointment?.doctor?.id || appointment?.doctor;

/**
 * Mueve una cita ya creada a otro horario del mismo médico.
 *
 * Reutiliza el mismo endpoint de slots que el modal de agendar, así que el
 * paciente elige entre exactamente los huecos que el backend va a aceptar. El
 * hueco que ocupa ahora la propia cita aparece libre: el backend se excluye a
 * sí misma del choque de horarios.
 */
function RescheduleAppointmentModal({ appointment, onClose, onRescheduled }) {
  const [selectedDate, setSelectedDate] = useState(getTodayInBogota());
  const [selectedTime, setSelectedTime] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const doctorId = getDoctorId(appointment);
  const doctorName = formatDoctorName(appointment?.doctor, 'tu médico');

  const { slots: availableSlots, loading: loadingSlots, error: slotsError } =
    useAvailableSlots(doctorId, selectedDate);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedTime || submitting) return;

    setSubmitting(true);
    setError('');

    try {
      const scheduledAt = slotToDate(selectedDate, selectedTime);
      const res = await appointmentService.rescheduleAppointment(
        appointment._id,
        scheduledAt.toISOString()
      );
      onRescheduled(res.data);
    } catch (err) {
      // El backend explica el motivo (slot ocupado, cita ya pagada...). Vale
      // mucho más que un "error al reagendar" genérico.
      setError(
        err.response?.data?.message
          || 'No se pudo reagendar la cita. Intenta con otro horario.'
      );
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto p-6">
        <div className="flex justify-between items-start mb-4">
          <div>
            <h2 className="text-xl font-bold text-gray-800">Reagendar cita</h2>
            <p className="text-sm text-gray-600 mt-1">Con {doctorName}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Cerrar"
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 mb-4 flex items-start gap-2">
          <Calendar className="h-4 w-4 text-gray-500 flex-shrink-0 mt-0.5" />
          <div className="text-sm">
            <p className="text-gray-600">Horario actual</p>
            <p className="font-semibold text-gray-800">
              {formatAppointmentDate(appointment.scheduledDate)}{' '}
              {formatAppointmentTime(appointment.scheduledDate)}
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label htmlFor="reschedule-date" className="block text-sm font-semibold text-gray-700 mb-2">
              Nueva fecha
            </label>
            <input
              id="reschedule-date"
              type="date"
              value={selectedDate}
              min={getTodayInBogota()}
              onChange={(e) => {
                setSelectedDate(e.target.value);
                setSelectedTime('');
              }}
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
              required
            />
          </div>

          <div className="mb-4">
            <p className="block text-sm font-semibold text-gray-700 mb-2">
              Nuevo horario (slots de 30 min)
            </p>

            <SlotPicker
              slots={availableSlots}
              loading={loadingSlots}
              error={slotsError}
              selectedTime={selectedTime}
              onSelect={setSelectedTime}
              disabled={submitting}
            />
          </div>

          {error && (
            <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2 mb-4">
              {error}
            </p>
          )}

          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 border border-gray-300 text-gray-700 py-2 rounded-lg hover:bg-gray-50 transition font-semibold"
            >
              Volver
            </button>
            <button
              type="submit"
              disabled={!selectedTime || submitting}
              className="flex-1 bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 transition font-semibold disabled:opacity-50"
            >
              {submitting ? 'Reagendando...' : 'Confirmar cambio'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default RescheduleAppointmentModal;
